import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EuroIcon from '@mui/icons-material/Euro';
import HttpsIcon from '@mui/icons-material/Https';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Badge,
  Box,
  Button,
  Container,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  checkAccessResponsible,
  checkRoleFi,
} from '../../../helpers/utils/RoleManager';
import parseSessionStorageVariable from '../../../helpers/utils/parseSessionStorage';
import useDashboardSC from '../../../hooks/dashboard/useDashboardSC';
import DashboardSCRefundTable from './DashboardSCRefundTable.jsx';
import DashboardSCTaskTable from './DashboardSCTaskTable.jsx';
import DashboardSCTicketTable from './DashboardSCTicketTable.jsx';
import { setSnackBar } from '../../../states/app/staticSlice.js';

const services = ['SCO', 'SLO', 'SAV', 'MP'];
const CONTENT_TAB = {
  TICKETS: 'Tickets',
  TASKS: 'Tasks',
  REFUNDS: 'Refunds',
};

const DashboardSC = () => {
  const profile = useSelector((state) => state?.login?.profile);
  const navigate = useNavigate();
  const [serviceTab, setServiceTab] = useState(
    parseSessionStorageVariable('service_tab') || services[0]
  ); // TODO : définir le service selon le profil de l'utilisateur (premier service trouvé en tant que responsable)
  const [contentTab, setContentTab] = useState(
    parseSessionStorageVariable('content_tab') || CONTENT_TAB.TICKETS
  );
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(0);
  const [assignedUserTicket, setAssignedUserTicket] = useState(
    parseSessionStorageVariable('assigned_user_dashboard_ticket')
  );
  const [assignedUserTask, setAssignedUserTask] = useState(
    parseSessionStorageVariable('assigned_user_dashboard_task')
  );
  const [creatorUserTask, setCreatorUserTask] = useState(
    parseSessionStorageVariable('creator_user_dashboard_task')
  );
  const [isClose, setIsClose] = useState(
    parseSessionStorageVariable('is_close_dashboard') || false
  );
  const [toModerate, setToModerate] = useState(
    parseSessionStorageVariable('to_moderate_dashboard') || false
  );
  const [sellerType, setSellerType] = useState(
    parseSessionStorageVariable('seller_type_dashboard') || null
  );
  const [orderRef, setOrderRef] = useState(
    parseSessionStorageVariable('order_ref_dashboard')
  );
  const [orderBy, setOrderBy] = useState(
    parseSessionStorageVariable('order_by_dashboard') || null
  );
  const [order, setOrder] = useState(
    parseSessionStorageVariable('order_dashboard')
  );
  const [isDone, setIsDone] = useState(
    parseSessionStorageVariable('is_done_dashboard') || false
  );
  const [ticketRef, setTicketRef] = useState(
    parseSessionStorageVariable('ticket_ref_dashboard')
  );
  const [responseOverdueFilter, setResponseOverdueFilter] = useState(
    parseSessionStorageVariable('response_overdue_filter_dashboard') || false
  );
  const [dashboardData, setDashboardData] = useState({
    // Common
    limit: limit,
    offset: offset,
    service: serviceTab, // SCO / SLO / SAV
    content: contentTab, // Tickets / Tasks
    // Tickets
    assignedUserTicket: assignedUserTicket, // null / "" /  / {User}
    isClose: isClose, // true / false
    toModerate: toModerate, // true / false
    sellerType: sellerType, // true / false / null
    orderRef: orderRef, // null / "" / "WEBC-FR202308-51219"
    orderBy: orderBy, // null / "status"
    order: order, // "asc" / "desc"
    // Tasks
    assignedUserTask: assignedUserTask, // null / "" / {User}
    creatorUserTask: creatorUserTask, // null / "" / {User}
    isDone: isDone, // true / false
    ticketRef: ticketRef, // null / "C-99999999"
    // MP overdue filter
    responseOverdueFilter: responseOverdueFilter, // true / false
  });
  const { datas, res, isValidating, isLoading, mutate } = useDashboardSC(
    contentTab,
    dashboardData
  );
  const dispatch = useDispatch();

  useEffect(() => {
    sessionStorage.setItem('is_close_dashboard', isClose);
  }, [isClose]);

  useEffect(() => {
    sessionStorage.setItem('to_moderate_dashboard', toModerate);
  }, [toModerate]);

  useEffect(() => {
    sessionStorage.setItem('seller_type_dashboard', sellerType);
  }, [sellerType]);

  useEffect(() => {
    sessionStorage.setItem('is_done_dashboard', isDone);
  }, [isDone]);

  useEffect(() => {
    sessionStorage.setItem('content_tab', contentTab);
  }, [contentTab]);

  useEffect(() => {
    sessionStorage.setItem('service_tab', serviceTab);
  }, [serviceTab]);

  useEffect(() => {
    sessionStorage.setItem('order_ref_dashboard', orderRef);
  }, [orderRef]);

  useEffect(() => {
    sessionStorage.setItem('order_by_dashboard', orderBy);
  }, [orderBy]);

  useEffect(() => {
    sessionStorage.setItem('order_dashboard', order);
  }, [order]);

  useEffect(() => {
    sessionStorage.setItem('ticket_ref_dashboard', ticketRef);
  }, [ticketRef]);

  useEffect(() => {
    sessionStorage.setItem(
      'response_overdue_filter_dashboard',
      responseOverdueFilter
    );
  }, [responseOverdueFilter]);

  useEffect(() => {
    sessionStorage.setItem(
      'assigned_user_dashboard_ticket',
      JSON.stringify(assignedUserTicket)
    );
  }, [assignedUserTicket]);

  useEffect(() => {
    sessionStorage.setItem(
      'assigned_user_dashboard_task',
      JSON.stringify(assignedUserTask)
    );
  }, [assignedUserTask]);

  useEffect(() => {
    sessionStorage.setItem(
      'creator_user_dashboard_task',
      JSON.stringify(creatorUserTask)
    );
  }, [creatorUserTask]);

  useEffect(() => {
    setDashboardData({
      // Common
      limit: limit,
      offset: offset,
      service: serviceTab, // SCO / SLO / SAV
      content: contentTab, // Tickets / Tasks
      // Tickets
      assignedUserTicket: assignedUserTicket, // null / "" / "user?._id"
      isClose: isClose, // true / false
      toModerate: toModerate, // true / false
      sellerType: sellerType, // true / false / null
      orderRef: orderRef, // null / "" / "WEBC-FR202308-51219"
      orderBy: orderBy, // null / "status"
      order: order, // "asc" / "desc"
      // Tasks
      assignedUserTask: assignedUserTask, // null / "" / "user?._id"
      creatorUserTask: creatorUserTask, // null / "" / "user?._id"
      isDone: isDone, // true / false
      ticketRef: ticketRef, // null / "C-99999999"
      // MP overdue filter
      responseOverdueFilter: responseOverdueFilter, // true / false
    });
  }, [
    serviceTab,
    contentTab,
    limit,
    offset,
    assignedUserTicket,
    assignedUserTask,
    creatorUserTask,
    isClose,
    toModerate,
    sellerType,
    orderRef,
    orderBy,
    order,
    isDone,
    ticketRef,
    responseOverdueFilter,
  ]);

  useEffect(() => {
    setOffset(page * limit);
  }, [page, limit]);

  useEffect(() => {
    setPage(0);
  }, [
    serviceTab,
    contentTab,
    assignedUserTicket,
    assignedUserTask,
    creatorUserTask,
    isClose,
    toModerate,
    sellerType,
    orderRef,
    isDone,
    ticketRef,
    responseOverdueFilter,
  ]);

  useEffect(() => {
    mutate();
    if (res && res?.status > 300) {
      const messageError = `Error: ${res?.status} - ${res?.statusText}: DashboardSC`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } else {
      dispatch(setSnackBar({ open: false, message: null, color: null }));
    }
  }, [dashboardData]);

  useEffect(() => {
    for (const role in profile?.roles) {
      if (services?.indexOf(role?.service?.code) !== -1) {
        setServiceTab(role?.service?.code);
        break;
      }
    }
  }, [profile]);

  const handleContentChange = (event, newValue) => {
    setContentTab(newValue);
  };

  const handleServiceTabChange = (event, value) => {
    setServiceTab(value);
  };

  const handleRedirect = () => {
    navigate('/inbox');
  };

  return checkAccessResponsible(profile) ? (
    <Container maxWidth="full">
      <Tabs
        value={serviceTab}
        onChange={handleServiceTabChange}
        sx={{ marginLeft: 6 }}
      >
        {services?.map((service) => {
          if (service === 'MP') {
            return (
              <Tab
                key={service}
                label={
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={!datas?.existingMPTickets}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {service}
                      {datas?.overdueMP?.length > 0 && (
                        <Tooltip title={`${datas.overdueMP.length} ticket(s) MP en retard de réponse`}>
                          <Badge
                            badgeContent={datas.overdueMP.length}
                            color="warning"
                            sx={{
                              '& .MuiBadge-badge': {
                                height: 'fit-content',
                                width: 'fit-content',
                              },
                            }}
                          >
                            <WarningAmberIcon
                              fontSize="small"
                              color="warning"
                            />
                          </Badge>
                        </Tooltip>
                      )}
                    </Box>
                  </Badge>
                }
                value={service}
                disabled={
                  contentTab === 'Tasks' ||
                  contentTab === 'Refunds' ||
                  checkRoleFi(profile)
                }
              />
            );
          } else {
            return (
              <Tab
                key={service}
                label={
                  <Badge
                    color="error"
                    badgeContent={datas ? datas[service] : 0}
                    invisible={
                      contentTab === CONTENT_TAB.TASKS ||
                      contentTab === CONTENT_TAB.TICKETS
                    }
                    sx={{
                      '& .MuiBadge-badge': {
                        height: 'fit-content',
                        width: 'fit-content',
                      },
                    }}
                  >
                    {service}
                  </Badge>
                }
                value={service}
                disabled={
                  contentTab === CONTENT_TAB.REFUNDS &&
                  !profile?.roles.some((role) => role.name.includes(service)) &&
                  profile?.permission?.level === 1 &&
                  !checkRoleFi(profile)
                }
              />
            );
          }
        })}
      </Tabs>
      <Box
        sx={{
          display: 'flex',
          marginLeft: -4,
        }}
      >
        <Tabs
          orientation="vertical"
          // variant="scrollable"
          value={contentTab}
          onChange={handleContentChange}
          sx={{ width: 80 }}
        >
          <Tab
            icon={<ConfirmationNumberIcon />}
            id={0}
            value={CONTENT_TAB.TICKETS}
            disabled={checkRoleFi(profile)}
            label={
              <>
                <Badge
                  color="error"
                  badgeContent={datas ? datas?.tickets?.length : 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      height: 'fit-content',
                      width: 'fit-content',
                    },
                  }}
                ></Badge>
              </>
            }
          />
          <Tab
            icon={<TaskAltIcon />}
            id={1}
            value={CONTENT_TAB.TASKS}
            disabled={checkRoleFi(profile)}
            label={
              <>
                <Badge
                  color="error"
                  badgeContent={datas ? datas?.tasks?.length : 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      height: 'fit-content',
                      width: 'fit-content',
                    },
                  }}
                ></Badge>
              </>
            }
          />
          {checkAccessResponsible(profile) || checkRoleFi(profile) ? (
            <Tab
              icon={<EuroIcon />}
              id={2}
              value={CONTENT_TAB.REFUNDS}
              label={
                <Badge
                  color="error"
                  variant="dot"
                  invisible={
                    datas?.SCO === 0 && datas?.SAV === 0 && datas?.SLO === 0
                  }
                  badgeContent={datas ? datas?.refunds?.length : 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      height: 'fit-content',
                      width: 'fit-content',
                    },
                  }}
                />
              }
              iconPosition={'start'}
            />
          ) : null}
        </Tabs>
        <Box sx={{ padding: '0.1rem', width: '112rem' }}>
          {contentTab === CONTENT_TAB.TICKETS ? (
            <DashboardSCTicketTable
              loading={isLoading}
              validating={isValidating}
              datas={datas?.data}
              service={serviceTab}
              dashboardData={dashboardData}
              setIsClose={setIsClose}
              setToModerate={setToModerate}
              setSellerType={setSellerType}
              setOrderRef={setOrderRef}
              setOrderBy={setOrderBy}
              setOrder={setOrder}
              setAssignedUserTicket={setAssignedUserTicket}
              setResponseOverdueFilter={setResponseOverdueFilter}
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              total={datas?.length || 0}
              mutate={mutate}
              titleIcon={<ConfirmationNumberIcon color="primary" />}
              overdueCount={datas?.overdueMP?.length || 0}
            />
          ) : null}
          {contentTab === CONTENT_TAB.TASKS ? (
            <DashboardSCTaskTable
              loading={isLoading}
              validating={isValidating}
              datas={datas?.data}
              service={serviceTab}
              dashboardData={dashboardData}
              setIsDone={setIsDone}
              setTicketRef={setTicketRef}
              setAssignedUserTask={setAssignedUserTask}
              setCreatorUserTask={setCreatorUserTask}
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              total={datas?.length || 0}
              mutate={mutate}
              titleIcon={<TaskAltIcon color="primary" />}
            />
          ) : null}
          {contentTab === CONTENT_TAB.REFUNDS ? (
            <DashboardSCRefundTable
              loading={isLoading}
              validating={isValidating}
              datas={datas?.data}
              mutate={mutate}
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              titleIcon={<EuroIcon color="primary" />}
              total={datas ? datas[serviceTab] : 0}
            />
          ) : null}
        </Box>
      </Box>
    </Container>
  ) : (
    <Box
      sx={{
        width: '100%',
        height: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'info.main',
        }}
      >
        <HttpsIcon sx={{ fontSize: 50 }} />
        <Typography>
          Hello, {profile?.firstName}... Vous ne devriez pas être ici...
        </Typography>
        <Button onClick={handleRedirect}>
          Veuillez cliquez ici pour retourner dans votre Inbox
        </Button>
      </Box>
    </Box>
  );
};

export default DashboardSC;
